import { Button, Card, IconButton, LuIcon, Separator } from "@overmap-ai/blocks"
import { memo, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { HEADING_LEVELS, TEXT_ALIGNMENTS } from "../constants"
import { HeadingLevel, TextAlign } from "../enums"
import { useHeadingState, useTextAlignState } from "../hooks"
import {
	FontSizeButton,
	HorizontalRuleButton,
	RedoIconButton,
	TextColorButton,
	TextHighlightButton,
	UndoIconButton,
	UnsetMarksButton,
} from "./Buttons"
import { HeadingDropdownSelect, LineHeightDropdownSelect, TextAlignDropdownSelect } from "./DropdownSelects"
import { FontSizeInput } from "./Inputs"
import { headingDataMapping, textAlignDataMapping, tipTapIconMapping } from "./mappings"
import { TextColorPickerPopover, TextHighlightPickerPopover } from "./Popovers"
import {
	BlockquoteToggleButton,
	BoldToggleButton,
	BulletListToggleButton,
	CodeBlockToggleButton,
	CodeToggleButton,
	ItalicToggleButton,
	OrderedListToggleButton,
	StrikeToggleButton,
	SubscriptToggleButton,
	SuperscriptToggleButton,
	TasklistToggleButton,
	UnderlineToggleButton,
} from "./ToggleButtons"
import type { EditorProps } from "./typings"

export const EditorToolbar = memo<EditorProps>((props) => {
	const { editor } = props

	const { textAlign } = useTextAlignState(editor)
	const { level } = useHeadingState(editor)

	const handleHeadingSelect = useCallback(
		(level?: HeadingLevel) => {
			if (level) {
				editor.chain().focus().toggleHeading({ level }).run()
			} else {
				editor.chain().focus().setParagraph().run()
			}
		},
		[editor],
	)

	const handleTextAlignSelect = useCallback(
		(alignment: TextAlign) => {
			editor.chain().focus().toggleTextAlign(alignment).run()
		},
		[editor],
	)

	return (
		<Card size="xs" variant="surface" className="p-1! flex w-full shrink-0 items-center gap-0.5 overflow-y-auto">
			<div className="flex w-max grow items-center gap-0.5">
				<UndoIconButton size="sm" accentColor="base" variant="ghost" />
				<RedoIconButton size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<FontSizeButton direction="decrease" size="sm" accentColor="base" variant="ghost" />
				<FontSizeInput variant="outline" size="sm" />
				<FontSizeButton direction="increase" size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<HeadingDropdownSelect
					required={false}
					levels={HEADING_LEVELS}
					value={level}
					onValueChange={handleHeadingSelect}
				>
					<Button size="sm" accentColor="base" variant="ghost">
						{level ? (
							<>
								<LuIcon icon={headingDataMapping[level].icon} />
								{headingDataMapping[level].label}
							</>
						) : (
							"Normal"
						)}
						<LuIcon icon={modelIconMapping.chevronDown} />
					</Button>
				</HeadingDropdownSelect>

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<BlockquoteToggleButton size="sm" accentColor="base" variant="ghost" />
				<CodeToggleButton size="sm" accentColor="base" variant="ghost" />
				<CodeBlockToggleButton size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<BoldToggleButton size="sm" accentColor="base" variant="ghost" />
				<ItalicToggleButton size="sm" accentColor="base" variant="ghost" />
				<UnderlineToggleButton size="sm" accentColor="base" variant="ghost" />
				<StrikeToggleButton size="sm" accentColor="base" variant="ghost" />
				<SubscriptToggleButton size="sm" accentColor="base" variant="ghost" />
				<SuperscriptToggleButton size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<TextColorPickerPopover size="xs" side="top">
					<TextColorButton size="sm" accentColor="base" variant="ghost" />
				</TextColorPickerPopover>

				<TextHighlightPickerPopover size="xs" side="top">
					<TextHighlightButton size="sm" accentColor="base" variant="ghost" />
				</TextHighlightPickerPopover>

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<BulletListToggleButton size="sm" accentColor="base" variant="ghost" />
				<OrderedListToggleButton size="sm" accentColor="base" variant="ghost" />
				<TasklistToggleButton size="sm" accentColor="base" variant="ghost" />
				<HorizontalRuleButton size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<TextAlignDropdownSelect
					required
					alignments={TEXT_ALIGNMENTS}
					value={textAlign}
					onValueChange={handleTextAlignSelect}
				>
					<Button size="sm" accentColor="base" variant="ghost">
						{textAlign ? (
							<>
								<LuIcon icon={textAlignDataMapping[textAlign].icon} />
								{textAlignDataMapping[textAlign].label}
							</>
						) : (
							"Align"
						)}
						<LuIcon icon={modelIconMapping.chevronDown} />
					</Button>
				</TextAlignDropdownSelect>

				<LineHeightDropdownSelect>
					<IconButton size="sm" accentColor="base" variant="ghost">
						<LuIcon icon={tipTapIconMapping.lineHeight} />
					</IconButton>
				</LineHeightDropdownSelect>

				<Separator orientation="vertical" size="sm" className="mx-1" />
			</div>

			<UnsetMarksButton size="sm" accentColor="base" variant="ghost" />
		</Card>
	)
})
EditorToolbar.displayName = "EditorToolbar"
